import React, { useMemo, useState } from "react";
import { Autocomplete, createFilterOptions, TextField, Input } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { CrfField, CrfFieldType, CrfListField, FieldOption, Laterality, OptionType } from "../nebula.service";
import DisplayText from "../../../components/DisplayText/DisplayText";
import { StyledTooltip } from "./StyledTooltip";
import { afterDecimal } from "../utils/afterDecimal";

interface EditFieldProps {
    field: CrfField;
    value: string;
    onChange: (value: string) => void;
    error?: string;
    autoFocus?: boolean;
    fullWidth?: boolean;
}

interface EditListFieldProps {
    field: CrfListField & { isRequired: boolean; laterality: Laterality; precision?: number; unit?: string };
    value: string;
    onChange: (value: string) => void;
    error?: string;
    autoFocus?: boolean;
    fullWidth?: boolean;
}

export const EditField: React.FC<EditFieldProps> = ({
    field,
    value,
    onChange,
    error,
    autoFocus = false,
    fullWidth = true,
}) => {
    switch (field.fieldType) {
        case CrfFieldType.string:
            return (
                <EditString
                    field={field}
                    value={value}
                    onChange={onChange}
                    error={error}
                    autoFocus={autoFocus}
                    fullWidth={fullWidth}
                />
            );
        case CrfFieldType.list:
            return (
                <EditOptions
                    field={field}
                    value={value}
                    onChange={onChange}
                    error={error}
                    autoFocus={autoFocus}
                    fullWidth={fullWidth}
                />
            );
        case CrfFieldType.date:
            return (
                <EditDate
                    field={field}
                    value={value}
                    onChange={onChange}
                    error={error}
                    autoFocus={autoFocus}
                    fullWidth={fullWidth}
                />
            );
        default:
            return (
                <EditNumber
                    field={field}
                    value={value}
                    onChange={onChange}
                    error={error}
                    autoFocus={autoFocus}
                    fullWidth={fullWidth}
                />
            );
    }
};

export const EditString: React.FC<EditFieldProps> = ({ field, value, onChange, error, autoFocus, fullWidth }) => {
    return (
        <StyledTooltip placement="top-start" open={!!error} title={error || ""}>
            <TextField
                fullWidth={fullWidth}
                multiline
                rows={2}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                size="small"
                error={!!error}
                autoFocus={autoFocus}
                sx={{ my: 1 }}
            />
        </StyledTooltip>
    );
};

export const EditNumber: React.FC<EditFieldProps> = ({ field, value, onChange, error, autoFocus, fullWidth }) => {
    return (
        <StyledTooltip placement="top-start" open={!!error} title={error || ""}>
            <TextField
                fullWidth={fullWidth}
                value={value}
                onChange={(e) => {
                    const newValue = e.target.value;

                    // Check precision if specified
                    if (field.precision !== undefined && newValue) {
                        const numValue = parseFloat(newValue);
                        if (!isNaN(numValue) && afterDecimal(numValue) > field.precision) {
                            return; // Don't update if precision exceeds limit
                        }
                    }

                    onChange(newValue);
                }}
                size="small"
                type="number"
                error={!!error}
                autoFocus={autoFocus}
                sx={{ my: 1 }}
            />
        </StyledTooltip>
    );
};

export const EditDate: React.FC<EditFieldProps> = ({ field, value, onChange, error, autoFocus, fullWidth }) => {
    return (
        <StyledTooltip placement="top-start" open={!!error} title={error || ""}>
            <DesktopDatePicker
                value={value ? new Date(value) : null}
                onChange={(newValue) => {
                    if (newValue) {
                        onChange(newValue.toISOString());
                    }
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size="small"
                        error={!!error}
                        autoFocus={autoFocus}
                        fullWidth={fullWidth}
                        sx={{ my: 1 }}
                    />
                )}
            />
        </StyledTooltip>
    );
};

export const EditOptions: React.FC<EditListFieldProps> = ({ field, value, onChange, error, autoFocus, fullWidth }) => {
    // Initialize inputValue with formatted value including unit if applicable
    const [inputValue, setInputValue] = useState(() => {
        const selectedOption = field.fieldOptions?.find((option: any) => option.value === value);
        if (selectedOption) {
            return selectedOption.label;
        }
        return value ? `${value}` : "";
    });

    const filter = createFilterOptions<any>({
        stringify: (option) => {
            // Convert the option value to string safely
            return String(option.label || option.value || "");
        },
    });
    // Get free input option if available
    const freeInputOption = field.fieldOptions?.find((option: any) => option.type === OptionType.input);

    // Create selectable options
    const selectableOptions: any[] = useMemo(() => {
        const fixedOptions = field.fieldOptions?.filter((option: any) => option.type !== OptionType.input) || [];

        if (freeInputOption) {
            const precisionHint = field.precision ? `x.${"x".repeat(field.precision)}` : "";
            fixedOptions.unshift({
                label: `Type a value ${field.unit ? `in ${field.unit}` : ""} ${
                    precisionHint ? `(${precisionHint})` : ""
                }`,
                value: "",
                // @ts-ignore
                disabled: true,
            });
        }

        return fixedOptions;
    }, [field.fieldOptions, field.precision, field.unit, freeInputOption]);

    // Get currently selected option
    const selectedOption = useMemo(
        () => selectableOptions.find((option: any) => option.value === value),
        [selectableOptions, value]
    );

    return (
        <StyledTooltip placement="top-start" open={!!error} title={error || ""}>
            <Autocomplete
                size="small"
                sx={{ my: 1, minWidth: fullWidth ? "100%" : 200 }}
                fullWidth={fullWidth}
                options={selectableOptions}
                getOptionLabel={(option) => {
                    // Handle both string and number values
                    if (option.inputValue) {
                        return String(option.inputValue);
                    }
                    // Regular option
                    return String(option.label);
                }}
                // @ts-ignore
                getOptionDisabled={(option) => !!option.disabled}
                value={selectedOption || null}
                inputValue={inputValue}
                onInputChange={(event, newInputValue, reason) => {
                    if (reason === "clear") {
                        setInputValue("");
                        onChange("");
                        return;
                    }

                    if (reason !== "reset") {
                        const isNumber = !isNaN(parseFloat(newInputValue));

                        if (!isNumber) {
                            setInputValue(newInputValue);
                            return;
                        }

                        if (
                            field.precision !== undefined &&
                            afterDecimal(parseFloat(newInputValue)) > field.precision
                        ) {
                            return;
                        }

                        setInputValue(newInputValue);
                    }
                }}
                onChange={(event, newValue: any) => {
                    if (newValue && newValue.value !== undefined) {
                        onChange(newValue.value);
                        // Update inputValue to match the selected option's label
                        const labelValue = !isNaN(parseFloat(newValue.label))
                            ? parseFloat(newValue.label).toString()
                            : newValue.label;
                        setInputValue(labelValue);
                    }
                }}
                filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    // Suggest the creation of a new value
                    if (freeInputOption) {
                        const { inputValue } = params;
                        const isExisting = options.some((option) => inputValue === option.label);

                        if (inputValue !== "" && !isExisting) {
                            return [
                                {
                                    value: Number(inputValue),
                                    label: `${inputValue} ${field.unit ?? ""}`,
                                },
                            ];
                        }
                    }

                    return filtered;
                }}
                renderOption={(props, option) => (
                    <li {...props}>
                        <DisplayText type="bodySmall" text={option.label} />
                    </li>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        error={!!error}
                        placeholder="Select or enter value"
                        variant="outlined"
                        autoFocus={autoFocus}
                    />
                )}
            />
        </StyledTooltip>
    );
};
